import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { User, ChevronDown, LogOut, Home } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_aianalytics-4/artifacts/d89kgck4_A_I_Means_Business.jpg";

/**
 * AuthenticatedPageLayout - Wrapper for informational pages when user is logged in
 * Keeps users within their authenticated session while viewing Pricing, About, FAQ, Contact pages
 */
const AuthenticatedPageLayout = ({ children }) => {
  const { user, logout } = useAuth();

  const handleLogout = () => {
    logout();
    window.location.href = "/";
  };

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      {/* Authenticated Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-navy-950/95 backdrop-blur border-b border-slate-800">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            {/* Logo - links to portal */}
            <Link to="/portal" className="flex items-center gap-2 flex-shrink-0">
              <img 
                src={LOGO_URL}
                alt="A.I. Means Business"
                className="h-12 sm:h-16 md:h-20 w-auto"
              />
            </Link>

            {/* Navigation Links + User Menu */}
            <div className="flex items-center gap-4 sm:gap-6">
              {/* Nav Links - hidden on mobile */}
              <div className="hidden md:flex items-center gap-6">
                <Link 
                  to="/portal/pricing" 
                  className="text-sm font-medium text-slate-400 hover:text-primary transition-colors"
                >
                  Pricing
                </Link>
                <Link 
                  to="/portal/about" 
                  className="text-sm font-medium text-slate-400 hover:text-primary transition-colors"
                >
                  About
                </Link>
                <Link 
                  to="/portal/faq" 
                  className="text-sm font-medium text-slate-400 hover:text-primary transition-colors"
                >
                  FAQ
                </Link>
                <Link 
                  to="/portal/contact" 
                  className="text-sm font-medium text-slate-400 hover:text-primary transition-colors"
                >
                  Contact
                </Link>
              </div>

              {/* Back to Portal Button */}
              <Link to="/portal">
                <Button
                  variant="outline"
                  size="sm"
                  className="border-slate-700 text-slate-300 hover:text-white hidden sm:flex"
                >
                  <Home className="w-4 h-4 mr-2" />
                  My Portal
                </Button>
              </Link>

              {/* User Dropdown Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-slate-700 text-slate-300 hover:text-white"
                    data-testid="auth-user-menu-btn"
                  >
                    <User className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">Account</span>
                    <ChevronDown className="w-4 h-4 ml-1 sm:ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-navy-900 border-slate-700">
                  <div className="px-2 py-1.5 text-xs text-slate-500">
                    {user?.email}
                  </div>
                  <DropdownMenuSeparator className="bg-slate-700" />
                  <DropdownMenuItem asChild>
                    <Link 
                      to="/portal" 
                      className="text-slate-300 hover:text-white hover:bg-navy-800 cursor-pointer flex items-center"
                    >
                      <Home className="w-4 h-4 mr-2" />
                      My Portal
                    </Link>
                  </DropdownMenuItem>
                  {/* Mobile Nav Links */}
                  <div className="md:hidden">
                    <DropdownMenuSeparator className="bg-slate-700" />
                    <DropdownMenuItem asChild>
                      <Link to="/portal/pricing" className="text-slate-300 hover:text-white hover:bg-navy-800">
                        Pricing
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/portal/about" className="text-slate-300 hover:text-white hover:bg-navy-800">
                        About
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/portal/faq" className="text-slate-300 hover:text-white hover:bg-navy-800">
                        FAQ
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/portal/contact" className="text-slate-300 hover:text-white hover:bg-navy-800">
                        Contact
                      </Link>
                    </DropdownMenuItem>
                  </div>
                  <DropdownMenuSeparator className="bg-slate-700" />
                  <DropdownMenuItem 
                    onClick={handleLogout}
                    className="text-slate-300 hover:text-white hover:bg-navy-800 cursor-pointer"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Log Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </nav>
      </header>

      {/* Main Content with top padding for fixed header */}
      <main className="pt-24 sm:pt-28 md:pt-32">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-navy-900 border-t border-slate-800 py-8">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <Link to="/portal" className="inline-block mb-4">
            <img 
              src={LOGO_URL}
              alt="A.I. Means Business"
              className="h-12 sm:h-16 w-auto mx-auto"
            />
          </Link>
          <p className="text-slate-500 text-xs">
            Copyright © 2026, Use What You Got Publishing, LLC • www.AIMeansBusiness.net
          </p>
        </div>
      </footer>
    </div>
  );
};

export default AuthenticatedPageLayout;
